import type React from 'react';
import { useCallback, useRef } from 'react';

import { SelectSearch } from '../SelectSearch';
import { mergePinnedOptions } from './pinnedOptions';
import type { EntitySelectorBaseProps } from './types';

export const EntitySelectorBase = <T,>({
	value,
	onChange,
	placeholder,
	disabled = false,
	error,
	allowClear = true,
	className,
	loadItems,
	loadItemById,
	mapToOption,
	excludeIds,
	pinnedOptions,
	recentGroupLabel,
}: EntitySelectorBaseProps<T>): React.JSX.Element => {
	// Keep a cache of loaded records to return with onChange
	const recordCacheRef = useRef<Map<number, T>>(new Map());

	const handleSearchItems = useCallback(
		async (query: string) => {
			try {
				const items = await loadItems(query);

				// Filter out excluded IDs if provided
				let filteredItems = items;

				if (excludeIds != null && excludeIds.length > 0) {
					filteredItems = items.filter((item) => {
						const option = mapToOption(item);

						return !excludeIds.includes(option.id);
					});
				}

				// Cache records and transform to SelectSearch format
				const loaded = filteredItems.map((item) => {
					const option = mapToOption(item);
					recordCacheRef.current.set(option.id, item);

					return {
						value: option.id,
						label: option.label,
						subtitle: option.subtitle,
					};
				});
				return mergePinnedOptions(pinnedOptions, loaded, query);
			} catch (err) {
				console.error('EntitySelectorBase: Failed to search items:', err);

				return [];
			}
		},
		[loadItems, mapToOption, excludeIds, pinnedOptions],
	);

	const handleLoadItemById = useCallback(
		async (id: string) => {
			try {
				const numericId = parseInt(id, 10);

				if (isNaN(numericId)) {
					return null;
				}

				const pinned = pinnedOptions?.find((option) => option.id === numericId);

				if (pinned != null) {
					return {
						value: pinned.id,
						label: pinned.label,
						subtitle: pinned.subtitle,
					};
				}

				const item = await loadItemById(numericId);

				if (item == null) {
					return null;
				}

				const option = mapToOption(item);
				recordCacheRef.current.set(option.id, item);

				return {
					value: option.id,
					label: option.label,
					subtitle: option.subtitle,
				};
			} catch (err) {
				console.error('EntitySelectorBase: Failed to load item by ID:', err);

				return null;
			}
		},
		[loadItemById, mapToOption, pinnedOptions],
	);

	const handleChange = useCallback(
		(selectedValue: string) => {
			if (selectedValue === '') {
				onChange(null, undefined);

				return;
			}

			const numericValue = parseInt(selectedValue, 10);

			if (isNaN(numericValue)) {
				onChange(null, undefined);

				return;
			}

			const cachedRecord = recordCacheRef.current.get(numericValue);
			onChange(numericValue, cachedRecord);
		},
		[onChange],
	);

	const handleLoadRecent = useCallback(
		async () => handleSearchItems(''),
		[handleSearchItems],
	);

	return (
		<SelectSearch
			mode="dynamic"
			value={value}
			onChange={handleChange}
			placeholder={placeholder}
			disabled={disabled}
			error={error != null}
			clearable={allowClear}
			className={className}
			searchItems={handleSearchItems}
			loadItemById={handleLoadItemById}
			loadRecent={handleLoadRecent}
			recentGroupLabel={recentGroupLabel}
			minSearchLength={1}
			searchDebounceMs={300}
		/>
	);
};
